import React from "react";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import {
  MultiDateInputContainerProps,
  DayType,
  WeekType,
} from "./MultiDateInput.types";
import { getPrimaryColor } from "customer/style/utils";
import { useJpHolidays } from "customer/hooks/useJpHolidays";
import { addDays, compareAsc, getDay, isSameDay } from "date-fns";
import { useConfig } from "customer/routes";

import { styleTheme } from '../../../style/theme'

export const MultiDateInput: React.FC<MultiDateInputContainerProps> = (
  props
) => {
  return <Component {...props} />;
};

const Component: React.FC<MultiDateInputContainerProps> = ({
  includeDates,
  selected,
  onChange,
  placeholder,
}) => {
  const DATE_RANGE = 90;
  const { primaryColor } = useConfig() as { primaryColor?: string };

  const holidays = useJpHolidays(new Date(), addDays(new Date(), DATE_RANGE));

  const isHoliday = (date: Date): boolean => {
    const day = getDay(date);
    const isHoliday = day === DayType.Sunday || day === DayType.Saturday;

    const isJPHoliday = holidays.some((holiday) => {
      const holidayDate = new Date(holiday.date);
      return isSameDay(holidayDate, date);
    });

    return isHoliday || isJPHoliday;
  };

  const isWeekday = (date: Date): boolean => {
    return !isHoliday(date);
  };

  const getUniqueAndSortedDates = (dates: Date[]): Date[] => {
    const uniqueDates = Array.from(
      new Set(dates.map((date) => date.getTime()))
    ).map((time) => new Date(time));
    return uniqueDates.sort(compareAsc);
  };

  const dateExistsInIncludeDates = (date: Date): boolean => {
    return includeDates.some((includedDate) => isSameDay(includedDate, date));
  };

  const createDates = (daysToAdd: number) => {
    const date = new Date();
    const startDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const endDate = addDays(startDate, daysToAdd);
    return { startDate, endDate };
  };

  const getDatesInRange = (
    type: WeekType,
    dateChecker: (date: Date) => boolean
  ): Date[] => {
    const { startDate, endDate } = createDates(DATE_RANGE);

    let dates: Date[] = [];
    for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      if (dateChecker(d) && dateExistsInIncludeDates(d)) {
        dates.push(new Date(d));
      }
    }

    if (type === WeekType.HOLIDAY) {
      const jpHoliday = holidays.filter((holiday) =>
        dateExistsInIncludeDates(holiday.date)
      );
      dates = getUniqueAndSortedDates([
        ...dates,
        ...jpHoliday.map((holiday) => new Date(holiday.date)),
      ]);
    }

    return dates;
  };

  // 単日選択
  const handleDateChange = (date: Date) => {
    if (dateExistsInIncludeDates(date)) {
      const newDates = (prevDates: Date[]) => {
        const dateExists = prevDates.some((d) => isSameDay(d, date));
        return dateExists
          ? prevDates.filter((d) => !isSameDay(d, date))
          : [...prevDates, date];
      };

      // 日付が土日祝かつ土日祝の特定日が選択されている場合、特定日選択の土日祝のボタンを非選択にする
      // 日付が平日かつ平日の特定日が選択されている場合、特定日選択の平日のボタンを非選択にする
      onChange({
        dates: getUniqueAndSortedDates(newDates(selected.dates)),
        weekday: isWeekday(date) && selected.weekday ? false : selected.weekday,
        holiday: isHoliday(date) && selected.holiday ? false : selected.holiday,
      });
    }
  };

  // 特定日選択
  const selectDates = (type: WeekType) => {
    const dateChecker = type === WeekType.WEEKDAY ? isWeekday : isHoliday;
    const isSelected =
      type === WeekType.WEEKDAY ? selected.weekday : selected.holiday;

    // 特定日選択を解除
    const deselectSpecificDate = (prevDates: Date[]) =>
      prevDates.filter((date) => !dateChecker(date));

    // 特定日選択を設定
    const selectSpecificDate = (prevDates: Date[]) => {
      const dates = getDatesInRange(type, dateChecker);
      const uniqueDates = dates.filter(
        (date) => !prevDates.some((prevDate) => isSameDay(prevDate, date))
      );
      return [...prevDates, ...uniqueDates];
    };

    onChange({
      dates: isSelected
        ? getUniqueAndSortedDates(deselectSpecificDate(selected.dates))
        : getUniqueAndSortedDates(selectSpecificDate(selected.dates)),
      weekday: type === WeekType.WEEKDAY ? !selected.weekday : selected.weekday,
      holiday: type === WeekType.HOLIDAY ? !selected.holiday : selected.holiday,
    });
  };

  const CustomContainer = ({ className, children }) => (
    <StyledCalendarContainer className={className} primaryColor={primaryColor}>
      {children}
      <div className="specific-date-wrapper">
        <p className="specific-date">特定日選択</p>
        <SelectedButton
          onClick={() => selectDates(WeekType.HOLIDAY)}
          selected={selected.holiday}
          primaryColor={primaryColor}
        >
          土日祝
        </SelectedButton>
        <SelectedButton
          onClick={() => selectDates(WeekType.WEEKDAY)}
          selected={selected.weekday}
          primaryColor={primaryColor}
        >
          平日
        </SelectedButton>
      </div>
    </StyledCalendarContainer>
  );

  return (
    <DatePicker
      locale="ja"
      dateFormat={"yyyy/MM/dd"}
      customInput={
        <StyledCustomInput type="text" primaryColor={primaryColor} />
      }
      placeholderText={`${placeholder || ""}(${selected.dates.length})`}
      calendarContainer={CustomContainer}
      includeDates={includeDates}
      dateFormatCalendar="yyyy年 M月"
      onChange={handleDateChange}
      highlightDates={selected.dates}
      holidays={holidays.map((holiday) => {
        return { date: holiday.date.toISOString(), holidayName: holiday.name };
      })}
      shouldCloseOnSelect={false}
      disabledKeyboardNavigation
      value={""}
    />
  );
};

const SelectedButton = styled.button<{
  selected: boolean;
  primaryColor?: string;
}>`
  background-color: ${(props) => (props.selected ? getPrimaryColor : "#fff")};
  color: ${(props) => (props.selected ? "#fff" : getPrimaryColor)};
  border: 1px solid ${getPrimaryColor};
  border-radius: 4px;
  padding: 8px 16px;
  margin-right: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  cursor: pointer;
`;

const StyledCalendarContainer = styled.div<{ primaryColor?: string }>`
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  border-color: #d9d9d9;
  padding: 24px;

  .react-datepicker {
    &__navigation--previous {
      transform: translate(63px, 22px);
    }

    &__navigation--next {
      right: 0;
      top: 24px;
      left: 228px;
    }

    &__month-container {
      position: relative;
      width: 280px;
    }

    &__month {
      margin: 0;
    }

    &__day {
      margin: 5px;
      color: ${styleTheme.colors.black};

      &[aria-label$="土曜日"] {
        color: #1e78ff;
      }

      &[aria-label$="日曜日"] {
        color: #e56274;
      }

      &--holidays {
        background-color: #fff;
        color: #e56274;
      }

      &[aria-disabled="true"][aria-label$="土曜日"],
      &[aria-disabled="true"][aria-label$="日曜日"] {
        color: #eee;
      }

      &--outside-month {
        visibility: hidden;
      }

      &--keyboard-selected,
      &--selected {
        color: #fff !important;
        background-color: ${getPrimaryColor};
      }

      &--disabled {
        color: #eee;
      }

      @media (hover: hover) {
        &[aria-disabled="false"]:hover {
          color: #fff;
          background-color: ${getPrimaryColor};
        }
      }

      @media (hover: none) {
        &[aria-disabled="false"]:active {
          color: #fff;
          background-color: ${getPrimaryColor};
        }
      }

      &--highlighted {
        color: #fff;
        background-color: ${getPrimaryColor};

        &[aria-label$="土曜日"] {
          color: #fff;
        }

        &[aria-label$="日曜日"] {
          color: #fff;
        }

        &--holidays {
          color: #fff;
        }
      }
    }

    &__time-container {
      width: auto;
      border-top: 1px solid #eee;
      border-left: none;
      padding-top: 12px;
      margin-left: 0;

      @media screen and (min-width: 768px) {
        padding-left: 24px;
        border-top: none;
        border-left: 1px solid #eee;
        margin-left: 24px;
      }
    }

    &__time-box {
      width: 280px !important;
    }

    &__time-list {
      display: flex;
      flex-wrap: wrap;

      &-item {
        padding: 0 !important;
        border: 1px solid ${getPrimaryColor};
        color: ${getPrimaryColor};
        width: 64px;
        line-height: 30px;
        border-radius: 4px;

        :hover {
          background-color: ${getPrimaryColor} !important;
          color: #fff;
        }
      }
    }

    &__header {
      background-color: #fff;
      border-bottom: none;

      &--time {
        padding-bottom: 20px;
      }
    }

    &__current-month {
      margin-bottom: 12px;
    }

    &-time__header {
      color: ${styleTheme.colors.black};
      text-align: left;
      font-weight: normal;
      font-size: 12px;
      overflow: visible;
    }

    &__current-month {
      color: ${styleTheme.colors.black};
    }

    &__day-name {
      margin: 5px;
      color: ${styleTheme.colors.black};
    }
  }

  .specific-date-wrapper {
    padding-top: 12px;

    .specific-date {
      padding-bottom: 8px;
    }
  }
`;

const StyledCustomInput = styled.input<{ primaryColor?: string }>`
  width: 100%;
  position: relative;
  background-color: #fff;
  padding: 13px 16px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  color: ${styleTheme.colors.black};
  font-size: 16px;
  caret-color: transparent;

  &::after {
    content: "";
    width: 8px;
    height: 8px;
    border-left: 2px solid ${styleTheme.colors.black};
    border-bottom: 2px solid ${styleTheme.colors.black};
    transform: rotate(-45deg) translate(1px, -1px);
    position: absolute;
    right: 16px;
    top: 20px;
  }

  &::placeholder {
    color: #363636;
  }

  :hover {
    border: 1px solid ${getPrimaryColor};
    color: #808080;
    cursor: pointer;
  }

  :focus {
    border: 2px solid ${getPrimaryColor};
    outline: none;
  }
`;
